/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useMemo } from 'react';
import { View } from 'react-native';
import {
  getColor,
  getTextStyle,
} from '@airasia-phoenix/react-native/utils/utils';
import { pd } from '@airasia-phoenix/react-native/utils';
import TextView, {
  SpannableTextProps,
} from '@airasia-phoenix/react-native/components/TextView';
import { FooterDisclaimer } from '../types';

interface DisclaimerTextProps {
  data: FooterDisclaimer;
  openUrl: (url: string) => void;
  onLinkPress?: (sectionTitle: string, linkText: string) => void;
}

export const DisclaimerText: React.FC<DisclaimerTextProps> = ({
  data,
  openUrl,
  onLinkPress,
}) => {
  const { links, text } = data;

  const { processedText, spannableTextsProps } = useMemo(() => {
    let processedText = text;
    const spannableTextsProps: SpannableTextProps[] = Object.keys(links).reduce<
      SpannableTextProps[]
    >((acc, key) => {
      const link = links[key];
      if (link) {
        processedText = processedText.replace(`{${key}}`, link.label);
        acc.push({
          spannable: link.label,
          highlightColor: getColor(pd.sys.colour.interactive_link),
          style: { textDecorationLine: 'none' },
          onPress: () => {
            onLinkPress?.('disclaimer', key);
            openUrl(link.url);
          },
        });
      }
      return acc;
    }, []);

    return { processedText, spannableTextsProps };
  }, [links, text, openUrl, onLinkPress]);

  return (
    <View>
      <TextView
        text={processedText}
        spannableTextsProps={spannableTextsProps}
        style={getTextStyle(
          pd.sys.typescale.caption_text,
          pd.sys.colour.content_primary,
        )}
        spannableParentTextStyle={[
          getTextStyle(
            pd.sys.typescale.caption_text,
            pd.sys.colour.interactive_link,
          ),
        ]}
        testID="footer-text"
      />
    </View>
  );
};

export default DisclaimerText;
